import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import { QK } from "../../base/qk";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import {
  getVaccinations,
  createVaccination,
  updateVaccination,
  deleteVaccination,
} from "../../api/services/ReferenceData/vaccination";

const PAGE_SIZE = 10;

const Vaccinations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const { data, isLoading } = useQuery({
    queryKey: [QK.vaccinations, page],
    queryFn: () => getVaccinations(page),
    keepPreviousData: true,
  });

  // Create mutation
  const createMutation = useMutation(createVaccination, {
    onSuccess: () => {
      enqueueSnackbar("Vaccination created successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.vaccinations);
      setName("");
    },
    onError: () => enqueueSnackbar("Failed to create vaccination", { variant: "error" }),
  });

  // Update mutation
  const updateMutation = useMutation(updateVaccination, {
    onSuccess: () => {
      enqueueSnackbar("Vaccination updated successfully!", { variant: "success" });
      queryClient.invalidateQueries(QK.vaccinations);
      setName("");
      setEditId(null);
    },
    onError: () => enqueueSnackbar("Failed to update vaccination", { variant: "error" }),
  });

  // Delete mutation
  const deleteMutation = useMutation(deleteVaccination, {
    onSuccess: () => {
      enqueueSnackbar("Vaccination deleted!", { variant: "success" });
      queryClient.invalidateQueries(QK.vaccinations);
      setConfirmDelete({ open: false, id: null });
    },
    onError: () => enqueueSnackbar("Failed to delete vaccination", { variant: "error" }),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }

    if (editId) {
      updateMutation.mutate({ id: editId, name });
    } else {
      createMutation.mutate({ name });
    }
  };

  const handlePageChange = (_, value) => setPage(value);

  if (isLoading) return <p>Loading...</p>;

  const totalPages = Math.ceil((data?.count || 0) / PAGE_SIZE);

  return (
    <Box>
      {/* Add / Update Form */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3, border: "1px solid #eee" }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", gap: 16 }}>
          <TextField
            label="Vaccination Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Button variant="contained" color="primary" type="submit">
            {editId ? "Update" : "Add"}
          </Button>
        </form>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell width={100}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.results?.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditId(item.id);
                        setName(item.name);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete({ open: true, id: item.id })}
                    >
                      <DeleteIcon color="error" fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
          </Box>
        )}
      </Paper>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        show={confirmDelete.open}
        title="Delete Vaccination"
        body="Are you sure you want to delete this vaccination?"
        handleClose={() => setConfirmDelete({ open: false, id: null })}
        handleConfirm={() => deleteMutation.mutate(confirmDelete.id)}
        isLoading={deleteMutation.isLoading}
        confirmText="Delete"
        cancelText="Cancel"
        isDelete
      />
    </Box>
  );
};

export default Vaccinations;